import { Box, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import {
  ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES,
  optimizationDefaultColDefs,
} from '@/constants/portfolioOptimization.constant'
import { useFileParser } from '@/hooks/useFileParser'
import { IRateFileRecord } from '@/interfaces/portfolioOptimization.interface'
import {
  cellValueFormatter,
  portfolioOptimizationDefaultGridOptions,
} from '@/utils/grid-utils/colDefs'
import { GridOptions, ValueFormatterParams } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import FileUploadBox from './FileUploadBox'

interface BorrowingRateGridProps {
  onChange: (records: IRateFileRecord[]) => void
  initialData?: IRateFileRecord[]
}

const BorrowingRateGrid: React.FC<BorrowingRateGridProps> = ({ onChange, initialData = [] }) => {
  const [fileRecords, setFileRecords] = useState<IRateFileRecord[]>(initialData)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const gridRef = useRef<AgGridReact>(null)

  const gridOptions: GridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    rowSelection: 'single' as const,
    columnDefs: [
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Time (years)',
        field: 'years',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: ValueFormatterParams) =>
          cellValueFormatter(params, 3),
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Rates (%)',
        field: 'rate',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: ValueFormatterParams) =>
          cellValueFormatter(params, 2),
      },
    ],
  }

  const handleFileSelect = async (file: File | null) => {
    setSelectedFile(file)
  }

  const { data, error, isLoading, parse } = useFileParser({
    file: selectedFile,
  })

  useEffect(() => {
    if (selectedFile) {
      parse()
    }
  }, [selectedFile])

  useEffect(() => {
    onChange(fileRecords)
  }, [fileRecords])

  useEffect(() => {
    if (data.length > 0 && !error && !isLoading) {
      const transformedData = data.map((d) => ({
        years: d.column1,
        rate: d.column2,
      }))
      setFileRecords(transformedData)
    }
  }, [error, isLoading, data])

  useEffect(() => {
    if (initialData && initialData.length > 0 && fileRecords.length === 0) {
      console.log('BorrowingRateGrid - Initializing with template data:', initialData)
      setFileRecords(initialData)
    }
  }, [initialData])

  return (
    <Box flex={1}>
      <Box marginBottom={2}>
        <Typography variant='h5'>Optimization Borrowing Rate</Typography>
        <Typography variant='body2'>
          Upload a File or manually enter the Optimization Borrowing Rate Values
          in the table below.
        </Typography>
        <FileUploadBox
          id='borrowing-rate-upload'
          onFileSelect={handleFileSelect}
          acceptedFormats={
            ACCEPTED_FILE_FORMATS_FOR_LIABILITY_REINVESTMENT_TAXES
          }
        />
      </Box>
      <Box className='ag-theme-quartz'>
        <AgGridReact
          ref={gridRef}
          rowData={fileRecords}
          gridOptions={gridOptions}
          data-cy='borrowing-rates-ag-grid'
        ></AgGridReact>
      </Box>
    </Box>
  )
}

export default BorrowingRateGrid
